import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="ui--confirm-submit"
//
// Asks before a submission that reaches past the record the form is about.
//
// A scope picker has one destructive direction: "apply to this racking leg" and
// "apply to its building" post the SAME payload, and only the second rewrites
// every leg in the building. The submit is the last moment the operator can
// still be told what is about to change, so the guard belongs there and not on
// the radio — picking a wider scope is not a mistake, it is half of a decision.
//
// The copy is the HOST's. This controller never composes a sentence: it takes
// `messageTemplate` (a translated string with one `%{label}` placeholder) and
// fills it with the chosen option's own visible label, which the host already
// translated. That keeps the gem free of a vocabulary it does not own and keeps
// every word server-rendered.
//
// Values:
//   guard           — the option values that need confirmation ([String]).
//                     Anything else submits straight through.
//   guardName       — `name` of the radio group to read ([String], required).
//   messageTemplate — the confirmation copy, `%{label}` standing for the
//                     chosen option's label ([String], required).
//   closeModals     — the OTHER dialogs to close once the operator confirms,
//                     by id ([String], default []). A confirmation is a step
//                     INSIDE a larger dialog (the sensitivity form's), and
//                     confirming it submits that form — so leaving it open
//                     while the page navigates away, or re-renders on a
//                     refusal, shows the operator a form they have already
//                     answered. This controller closes its OWN modal and
//                     anything named here, and never reaches for a parent it
//                     was not told about: the gem knows nothing about hosts'
//                     dialog nesting.
//
// Targets:
//   modal           — the <dialog> to open (a ui--modal element)
//   message         — element whose textContent receives the filled template.
//                     It is rendered with the copy already in it, so the text
//                     is correct before this controller runs.
//
// Actions (wire on the modal's own buttons, which must live inside this
// controller's element so the action resolves):
//   ui--confirm-submit#cancel   — close, leave the form unsubmitted
//   ui--confirm-submit#confirm  — close, submit for real
export default class extends Controller {
  static targets = ["modal", "message"]
  static values = {
    guard: Array,
    guardName: String,
    messageTemplate: String,
    closeModals: { type: Array, default: [] },
  }

  connect() {
    this._submitHandler = this._handleSubmit.bind(this)
    this.element.addEventListener("submit", this._submitHandler)
  }

  disconnect() {
    this.element.removeEventListener("submit", this._submitHandler)
  }

  // --- Form submit interception ---

  _handleSubmit(event) {
    // Second pass: confirm() re-submits, and this time it must go through.
    if (this._confirmed) return

    const choice = this._guardChoice()
    if (choice === null) return

    event.preventDefault()
    this._paintMessage(choice)
    this._openModal()
  }

  // --- Modal action handlers ---

  cancel() {
    this._closeModal()
  }

  confirm() {
    this._closeModal()
    this._closeNamedModals()
    // requestSubmit() dispatches `submit` synchronously, so the flag only has
    // to live for that one call. Clearing it afterwards — also when constraint
    // validation cancels the submit and no event fires — keeps the NEXT submit
    // from this still-connected form (a refusal re-rendered in place) guarded.
    this._confirmed = true
    try {
      this.element.requestSubmit()
    } finally {
      this._confirmed = false
    }
  }

  // --- Internal helpers ---

  // The dialogs the host named as enclosing this confirmation, closed on
  // confirm only — Cancel leaves them exactly as they were, because a
  // cancelled confirmation has changed nothing.
  //
  // Each is closed through its OWN ui--modal controller when it has one, so
  // the close is the same animated, scroll-unlocking, focus-restoring path
  // the X and Escape take; a bare `dialog.close()` would leave
  // `body.overflow = "hidden"` behind. A missing id or a dialog with no
  // controller is skipped rather than fatal: this is presentation, and the
  // submit it accompanies is the point.
  _closeNamedModals() {
    this.closeModalsValue.forEach((id) => {
      const element = document.getElementById(id)
      if (!element) return

      const controller = this.application.getControllerForElementAndIdentifier(
        element,
        "ui--modal",
      )
      if (controller) {
        controller.close()
      } else if (element.open) {
        element.close()
      }
    })
  }

  // The checked option, but only when it is one the host asked to confirm.
  // No selection (or an unconfirmed one) reads as null: the form speaks for
  // itself rather than this controller inventing a reason to stop it.
  _guardChoice() {
    const name = this.guardNameValue
    if (!name) return null

    const input = this.element.querySelector(
      `input[type="radio"][name="${CSS.escape(name)}"]:checked`,
    )
    if (!input) return null

    const value = String(input.value)

    return this.guardValue.includes(value) ? input : null
  }

  // The label the operator actually clicked, so the confirmation can name the
  // scope in the host's own words. `label[for]` is how a radio is named; the
  // raw value is the fallback for a form that renders one without a label.
  _labelFor(input) {
    if (!input.id) return String(input.value)

    const label = this.element.querySelector(`label[for="${CSS.escape(input.id)}"]`)
    const text = label?.textContent?.trim()

    return text || String(input.value)
  }

  _paintMessage(input) {
    if (!this.hasMessageTarget) return

    this.messageTarget.textContent = this.messageTemplateValue.replace("%{label}", this._labelFor(input))
  }

  _openModal() {
    if (!this.hasModalTarget) return

    const controller = this.application.getControllerForElementAndIdentifier(
      this.modalTarget,
      "ui--modal",
    )
    if (controller) {
      controller.open()
    } else if (typeof this.modalTarget.showModal === "function") {
      this.modalTarget.showModal()
    }
  }

  _closeModal() {
    if (!this.hasModalTarget) return

    const controller = this.application.getControllerForElementAndIdentifier(
      this.modalTarget,
      "ui--modal",
    )
    if (controller) {
      controller.close()
    } else if (this.modalTarget.open) {
      this.modalTarget.close()
    }
  }
}
